import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import type { VisibleColumnSpec } from "@/types";

/**
 * Resolve visible columns for a table by mapping `visibleColumns` onto `baseColumns`.
 *
 * Generic parameters:
 * - `T` — row type for the table (e.g. `Task`, `Order`).
 * - `Custom` — optional union of UI-only column ids (e.g. `"statusIcon" | "actions"`).
 *
 * @param baseColumns - The full set of `ColumnDef<T>[]` available for the table.
 * @param visibleColumns - Optional list of `VisibleColumnSpec<T, Custom>[]` that controls order and optional breakpoint overrides.
 *                         Each entry can be either a column id (`string`) or an object `{ id: string; breakpoint?: string }`.
 * @returns Filtered and ordered `ColumnDef<T>[]` based on `visibleColumns`. If no `visibleColumns` provided, returns `baseColumns`.
 *
 * @example
 * const columns = useVisibleColumns<Task, "statusIcon" | "actions">(baseColumns, visibleColumns);
 */
export function useVisibleColumns<T, Custom extends string = never>(
  baseColumns: ColumnDef<T>[],
  visibleColumns?: VisibleColumnSpec<T, Custom>[]
): ColumnDef<T>[] {
  return useMemo(() => {
    if (!visibleColumns || visibleColumns.length === 0) return baseColumns;

    const entries = visibleColumns.map((v): [string, string[] | undefined] =>
      typeof v === "string"
        ? [v, undefined]
        : [
            String(v.id),
            v.breakpoint ? (Array.isArray(v.breakpoint) ? v.breakpoint : [String(v.breakpoint)]) : undefined,
          ]
    );

    const getColKey = (col: ColumnDef<T>) => String(col.id ?? "");

    return entries
      .map(([id, overrideBps]) => {
        const col = baseColumns.find((c) => getColKey(c) === id);
        if (!col) return null;
        if (!overrideBps) return col;
        return {
          ...col,
          meta: {
            ...(col.meta || {}),
            breakpoint: overrideBps,
          },
        } as ColumnDef<T>;
      })
      .filter(Boolean) as ColumnDef<T>[];
  }, [baseColumns, visibleColumns]);
}
